/*****************************************************************************
 * Copyright(c) 2025 Qorus Inc
 * All rights reserved
 *****************************************************************************
 * NAME: src/utils/pkceUtils.ts
 * DEVELOPER: Jaime Rivera
 * DESCRIPTION: PKCE (Proof Key for Code Exchange) utility functions
 * REVISIONS:
 * Date Change      ID              Author              Description
 * -----------      -----------     --------------      ------------------------------------
 * 20-Nov-2025      HOTFIX-PKCE    Jaime Rivera        Initial implementation
 *****************************************************************************/

 interface PKCEConfig {
    codeVerifier: string;
    codeChallenge: string;
    state: string;
  }
 function generateRandomString(length: number): string {
   const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
   let result = '';
   const randomValues = new Uint8Array(length);
   crypto.getRandomValues(randomValues);
   
   for (let i = 0; i < length; i++) {
     result += charset[randomValues[i] % charset.length];
   }
   
   return result;
 }
 
 function generateCodeVerifier(): string {
   return generateRandomString(128);
 }
 
 async function generateCodeChallenge(codeVerifier: string): Promise<string> {
   const encoder = new TextEncoder();
   const data = encoder.encode(codeVerifier);
   const digest = await crypto.subtle.digest('SHA-256', data);
   
   const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
   return base64
     .replace(/\+/g, '-')
     .replace(/\//g, '_')
     .replace(/=/g, '');
 }
 
 function generateState(): string {
   return generateRandomString(32);
 }
 
 export async function generatePKCEConfig(): Promise<PKCEConfig> {
   const codeVerifier = generateCodeVerifier();
   const codeChallenge = await generateCodeChallenge(codeVerifier);
   const state = generateState();
   
   return {
     codeVerifier,
     codeChallenge,
     state
   };
 }
 
 export function isPKCESupported(): boolean {
   return typeof crypto !== 'undefined' && 
          typeof crypto.subtle !== 'undefined' && 
          typeof crypto.getRandomValues !== 'undefined';
 }